/**
 * BuchhaltungMahnungen — sevdesk-tarzı Mahnungen listesi.
 *
 * Otomatik akış (sayfa açılışında Mahnlauf koşar, idempotent):
 *   Fatura vadesi geçti  → Zahlungserinnerung (ücretsiz, +3 gün)
 *   Erinnerung vadesi geçti → 1. Mahnung (%8 netto, +7 gün)
 *   1. Mahnung vadesi geçti → 2. Mahnung (%8 netto, +7 gün)
 *   2. Mahnung vadesi geçti → "An Inkasso übergeben" (manuel onay)
 * Her satırda PDF + E-Mail gönderimi.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import PageHeader from '@/components/ui/PageHeader';
import PageShell from '@/components/ui/PageShell';
import {
  BellRing, Search, Download, Mail, Loader2, MoreHorizontal,
  ChevronLeft, ChevronRight, Gavel, Ban,
} from 'lucide-react';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useAuth } from '@/api/AuthContext';
import {
  runMahnlauf, listMahnungen, stornoMahnung, uebergabeInkasso, fmtEUR,
} from '@/lib/buchhaltungApi';
import { downloadMahnungPdf, sendMahnungEmail } from '@/lib/mahnwesen';

const PAGE_SIZE = 25;

const STATUS_TABS = [
  { key: 'alle', label: 'Alle' },
  { key: 'offen', label: 'Offen' },
  { key: 'faellig', label: 'Fällig' },
  { key: 'bezahlt', label: 'Bezahlt' },
  { key: 'inkasso', label: 'Inkasso' },
  { key: 'storniert', label: 'Storniert' },
];

const STATUS_BADGES = {
  offen: { label: 'Offen', class: 'bg-blue-50 text-blue-700' },
  faellig: { label: 'Fällig', class: 'bg-red-50 text-red-700' },
  bezahlt: { label: 'Bezahlt', class: 'bg-emerald-50 text-emerald-700' },
  inkasso: { label: 'Inkasso', class: 'bg-purple-50 text-purple-700' },
  storniert: { label: 'Storniert', class: 'bg-slate-100 text-slate-500' },
};

export default function BuchhaltungMahnungen() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('alle');
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [inkassoReady, setInkassoReady] = useState([]);
  const ranRef = useRef(false);

  const { data: mahnungen = [], isLoading, refetch } = useQuery({
    queryKey: ['fibu-mahnungen'],
    queryFn: listMahnungen,
  });

  // Otomatik Mahnlauf — sayfa açılışında bir kez (DB idempotens korumalı)
  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    (async () => {
      try {
        const { created, inkassoReady: ready } = await runMahnlauf(user?.email);
        setInkassoReady(ready);
        if (created.length > 0) {
          toast.info(`Mahnlauf: ${created.length} neue Stufe(n) automatisch erstellt.`);
          refetch();
          queryClient.invalidateQueries(['fibu-open-items']);
        }
      } catch (err) {
        toast.error('Mahnlauf-Fehler: ' + err.message);
      }
    })();
  }, [user?.email, refetch, queryClient]);

  const filtered = useMemo(() => {
    const t = searchTerm.toLowerCase();
    return mahnungen
      .filter((m) => statusFilter === 'alle' || m.status === statusFilter)
      .filter((m) =>
        !t ||
        (m.partner_name || '').toLowerCase().includes(t) ||
        (m.invoice_no || '').toLowerCase().includes(t)
      );
  }, [mahnungen, statusFilter, searchTerm]);

  const pageRows = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));

  const sendEmail = async (m) => {
    setBusyId(m.id);
    try {
      const to = await sendMahnungEmail(m);
      refetch();
      toast.success(`${m.level_name} per E-Mail an ${to} gesendet.`);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const doInkasso = async (m) => {
    if (!window.confirm(
      `Forderung ${m.invoice_no} (${fmtEUR(m.total_due)} inkl. Gebühren) an das Inkassounternehmen übergeben?`
    )) return;
    setBusyId(m.id);
    try {
      await uebergabeInkasso(m.open_item_id, user?.email);
      refetch();
      queryClient.invalidateQueries();
      toast.success('An Inkasso übergeben — Inkasso-Beleg erstellt.');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setBusyId(null);
    }
  };

  const canInkasso = (m) =>
    m.level_no === 2 && m.status === 'faellig' &&
    !mahnungen.some((x) => x.open_item_id === m.open_item_id && x.level_no === 3);

  return (
    <PageShell
      head={(
        <div className="flex items-center gap-2">
          <PageHeader
            icon={<BellRing className="w-5 h-5" />}
            title="Mahnungen"
            subtitle={`${mahnungen.length} Vorgänge`}
          />
          {inkassoReady.length > 0 && (
            <Badge className="text-[10px] bg-purple-100 text-purple-700 border-purple-200">
              {inkassoReady.length} Inkasso-bereit
            </Badge>
          )}
        </div>
      )}
    >

      {/* Durum sekmeleri */}
      <div className="flex gap-1 overflow-x-auto -mx-4 px-4 pb-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" role="tablist">
        {STATUS_TABS.map((t) => {
          const count = t.key === 'alle' ? mahnungen.length : mahnungen.filter((m) => m.status === t.key).length;
          const active = statusFilter === t.key;
          return (
            <button
              key={t.key} type="button" role="tab" aria-selected={active}
              onClick={() => { setStatusFilter(t.key); setPage(0); }}
              className={cn(
                'flex-shrink-0 min-h-[40px] px-3.5 rounded-full text-xs font-semibold border transition-colors cursor-pointer',
                active ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-600 border-slate-200 hover:border-slate-400'
              )}
            >
              {t.label} {count > 0 && <span className={active ? 'text-white/60' : 'text-slate-400'}>({count})</span>}
            </button>
          );
        })}
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Kunde oder Rechnungsnummer…"
          className="pl-10 h-11 bg-white"
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
        />
      </div>

      {/* Tablo (masaüstü başlığı) */}
      <Card className="overflow-hidden">
        <div className="hidden md:grid grid-cols-[90px_100px_110px_1fr_90px_110px_110px_88px] gap-2 px-3 py-2.5 bg-slate-50 border-b text-[10px] font-bold uppercase text-slate-500">
          <span>Status</span><span>Fälligkeit</span><span>Rechnungsnr.</span>
          <span>Kunde</span><span>Datum</span>
          <span className="text-right">Betrag (netto)</span>
          <span className="text-right">Offen (brutto)</span>
          <span />
        </div>

        {isLoading && (
          <p className="p-10 text-center text-sm text-slate-400">
            <Loader2 className="w-6 h-6 mx-auto mb-2 animate-spin" /> Lade Mahnungen…
          </p>
        )}
        {!isLoading && pageRows.length === 0 && (
          <p className="p-10 text-center text-sm text-slate-400">
            Keine Mahnungen — überfällige Rechnungen erscheinen hier automatisch.
          </p>
        )}

        <div className="divide-y divide-slate-100">
          {pageRows.map((m) => {
            const badge = STATUS_BADGES[m.status];
            const busy = busyId === m.id;
            const paid = m.status === 'bezahlt' || m.status === 'storniert';
            return (
              <div key={m.id} className="px-3 py-2.5 md:grid md:grid-cols-[90px_100px_110px_1fr_90px_110px_110px_88px] md:gap-2 md:items-center">
                {/* Status */}
                <span className={cn('inline-flex w-fit text-[10px] font-bold px-2 py-0.5 rounded-full', badge.class)}>
                  {badge.label}
                </span>
                {/* Fälligkeit */}
                <span className={cn('block text-xs text-slate-600 mt-1 md:mt-0', paid && 'line-through text-slate-400')}>
                  {m.due_date ? new Date(m.due_date).toLocaleDateString('de-DE') : '—'}
                </span>
                {/* Rechnungsnr */}
                <span className="block font-mono text-xs font-semibold text-slate-700 whitespace-nowrap truncate max-w-[140px]">{m.invoice_no || '—'}</span>
                {/* Kunde + kademe alt satırı */}
                <span className="block min-w-0">
                  <span className="block text-sm font-semibold text-slate-900 truncate">{m.partner_name || '—'}</span>
                  <span className="block text-[11px] text-slate-400 truncate">
                    {m.level_name} — Rechnung {m.invoice_no || ''}
                    {m.email_sent_at ? ` · ✉ ${new Date(m.email_sent_at).toLocaleDateString('de-DE')}` : ''}
                  </span>
                </span>
                {/* Datum */}
                <span className="block text-xs text-slate-600">
                  {new Date(m.created_at).toLocaleDateString('de-DE')}
                </span>
                {/* Betrag netto */}
                <span className="block text-xs font-mono text-slate-700 md:text-right whitespace-nowrap">
                  {fmtEUR(paid ? 0 : m.net_amount)}
                </span>
                {/* Offen brutto (inkl. Gebühren) */}
                <span className={cn('block text-xs font-mono font-bold md:text-right whitespace-nowrap', m.current_open > 0 && !paid ? 'text-slate-900' : 'text-slate-400')}>
                  {fmtEUR(paid ? 0 : m.current_open)}
                </span>
                {/* Aksiyonlar */}
                <span className="flex items-center justify-end gap-0.5 mt-1.5 md:mt-0">
                  <Button size="icon" variant="ghost" aria-label="PDF herunterladen"
                    className="h-9 w-9 text-slate-500 hover:text-slate-900"
                    onClick={() => downloadMahnungPdf(m)}>
                    <Download className="w-4 h-4" />
                  </Button>
                  <Button size="icon" variant="ghost" aria-label="Per E-Mail senden"
                    className={cn('h-9 w-9', m.email_sent_at ? 'text-emerald-600' : 'text-slate-500 hover:text-slate-900')}
                    disabled={busy || paid}
                    title={m.partner_email ? `Senden an ${m.partner_email}` : 'Keine E-Mail hinterlegt'}
                    onClick={() => sendEmail(m)}>
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />}
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button size="icon" variant="ghost" aria-label="Weitere Aktionen"
                        className="h-9 w-9 text-slate-500 hover:text-slate-900">
                        <MoreHorizontal className="w-4 h-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      {canInkasso(m) && (
                        <DropdownMenuItem className="text-purple-700 gap-2" onClick={() => doInkasso(m)}>
                          <Gavel className="w-4 h-4" /> An Inkasso übergeben
                        </DropdownMenuItem>
                      )}
                      {!m.storniert && (
                        <DropdownMenuItem className="text-red-600 gap-2"
                          onClick={async () => {
                            if (window.confirm(`${m.level_name} für ${m.invoice_no} stornieren?`)) {
                              await stornoMahnung(m.id);
                              refetch();
                              toast.success('Mahnung storniert.');
                            }
                          }}>
                          <Ban className="w-4 h-4" /> Stornieren
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </div>
            );
          })}
        </div>

        {/* Sayfalama */}
        {filtered.length > PAGE_SIZE && (
          <div className="flex items-center justify-center gap-3 py-3 border-t bg-slate-50/50">
            <Button size="icon" variant="ghost" aria-label="Vorherige Seite" className="h-9 w-9"
              disabled={page === 0} onClick={() => setPage(page - 1)}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span className="text-xs text-slate-600">
              {page * PAGE_SIZE + 1} – {Math.min((page + 1) * PAGE_SIZE, filtered.length)} von {filtered.length}
            </span>
            <Button size="icon" variant="ghost" aria-label="Nächste Seite" className="h-9 w-9"
              disabled={page >= pageCount - 1} onClick={() => setPage(page + 1)}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        )}
      </Card>

      <p className="text-[10px] text-slate-400">
        Automatik: Fällige Rechnung → Zahlungserinnerung (3 Tage, kostenfrei) → 1. Mahnung
        (8 % Gebühr auf Netto, 7 Tage) → 2. Mahnung (erneut 8 %, 7 Tage) → Inkasso-Übergabe.
        Der Mahnlauf startet bei jedem Seitenaufruf automatisch.
      </p>
    </PageShell>
  );
}
